import React, {
  useCallback,
  useMemo,
  useContext,
  useRef,
  useState,
  useEffect,
} from "react";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry } from "ag-grid-community";
import AppContext from "../Store/AppContext";
import { getUrl } from "../Network/urls";
import Tooltip from "@mui/material/Tooltip";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import {
  PaginationModule,
  ContextMenuModule,
  ServerSideRowModelModule,
  TextFilterModule,
  NumberFilterModule,
  ValidationModule,
  ColumnApiModule,
  ScrollApiModule,
  ColumnMenuModule,
  SetFilterModule,
  FiltersToolPanelModule,
  ColumnsToolPanelModule,
  IntegratedChartsModule,
  PivotModule,
  RowGroupingPanelModule,
  ColumnAutoSizeModule,
} from "ag-grid-enterprise";
import { CsvExportModule } from "ag-grid-community";
import NotSubscribedMessageComponent from "./UI/NotSubscribedMessageComponent";
import CircularProgress from "@mui/material/CircularProgress";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import "../Assets/css/euRecordKeepingAnalytics.css";
import { AgChartsEnterpriseModule } from "ag-charts-enterprise";
import { get } from "../Network/requests";
import toast from "react-hot-toast";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
} from "@mui/material";
import { TextField } from "@mui/material";
import { Slider, Box } from "@mui/material";
import { TbArrowsMinimize } from "react-icons/tb";
import { LuExpand } from "react-icons/lu";
import GridRowDetailsExpand from "./UI/GridRowDetailsExpand";
import GridGroupIcons from "./UI/GridGroupIcons";
import CustomDatepicker from "./UI/CustomDatepicker";
import { tokenExpire } from "../Utils";
import { useNavigate } from "react-router-dom";
import GridDataNotFound from "./UI/GridDataNotFound";
import IconButton from "@mui/material/IconButton";
import DynamicTabSwitcher from "./UI/DynamicTabSwitcher";
import { RTSKPICalculation } from "./UI/KpiCalculationModal";
import { FaInfoCircle } from "react-icons/fa";

// Register AG Grid Enterprise Modules
ModuleRegistry.registerModules([
  PaginationModule,
  ContextMenuModule,
  ServerSideRowModelModule,
  TextFilterModule,
  NumberFilterModule,
  ValidationModule,
  CsvExportModule,
  ColumnApiModule,
  ScrollApiModule,
  ColumnMenuModule,
  SetFilterModule,
  FiltersToolPanelModule,
  ColumnsToolPanelModule,
  IntegratedChartsModule.with(AgChartsEnterpriseModule),
  PivotModule,
  RowGroupingPanelModule,
  ColumnAutoSizeModule,
]);

function UKRecordKeepingRTS24OrdersAndExecutionsComponent({
  productCode,
  regulationCode,
  isActive,
  dateRange,
  setDateRange,
}) {
  // Get client details from context
  const {
    clientDetails,
    handleAPIEncryptValue,
    handleDecryptResponse,
    handleEncryptValue,
  } = useContext(AppContext);
  const gridRef = useRef();
  const navigate = useNavigate();
  const [isRegulationAccessible, setIsRegulationAccessible] = useState(false);
  const [gridApi, setGridApi] = useState(null);

  const [startDate, endDate] = dateRange;
  // Ref to hold the debounce timer for filter refresh
  const filterChangedTimeout = useRef(null);
  // Ref to store previous active state for asynchronous filters (first 4 columns)
  const prevAsyncActiveState = useRef({});
  // console.log("udpated start date :", startDate);
  // console.log("udpated end Date :", endDate);
  // URL endpoints from your configuration
  const filterUrl = getUrl("get-falsk-filter-values");
  const downloadUrl = getUrl("get-download-csv");
  const [isDownloading, setIsDownloading] = useState(false); // <-- Download state
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [showContactSupport, setShowContactSupport] = useState(false);
  const [showSupportDialog, setShowSupportDialog] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [rowRange, setRowRange] = useState([100000, 500000]);
  const [rowRangeLimits, setRowRangeLimits] = useState({
    min: 100000,
    max: 500000,
  });

  // KPI States
  const emptyKpis = {
    totalRecs: 0,
    totalOrds: 0,
    totalExe: 0,
    valid: 0,
    validOrds: 0,
    validExe: 0,
    validPctOfTotal: 0,
    invalid: 0,
    invalidOrds: 0,
    invalidExe: 0,
    invalidPctOfTotal: 0,
    excluded: 0,
    excludedOrds: 0,
    excludedExe: 0,
    excludedPctOfTotal: 0,
    accuracyPct: 0,
    accuracyOrdsPct: 0,
    accuracyExePct: 0,
    errorPct: 0,
    errorOrdsPct: 0,
    errorExePct: 0,
  };
  const [kpiData, setKpiData] = useState(emptyKpis);
  const [kpiLoading, setKpiLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [selectedRow, setSelectedRow] = useState({});
  const [isDetailBoxOpen, setIsDetailBoxOpen] = useState(false);
  const [hoverPos, setHoverPos] = useState(null); // {x, y}
  const [showDialog, setShowDialog] = useState(false);
  const timerRef = useRef("");

  const TAB_MAP = {
    rcd: "Records",
    err: "Errors",
  };
  const [activeTab, setActiveTab] = useState(Object.keys(TAB_MAP)[0]);
  const activeTabRef = useRef(Object.keys(TAB_MAP)[0]);
  const [activeKpiKey, setActiveKpiKey] = useState(null);

  const handleTabChange = (tab) => {
    activeTabRef.current = tab;
    setActiveTab(tab);
  };

  // Refresh grid when activeTab changes
  useEffect(() => {
    if (gridApi && activeTab) {
      gridApi.refreshServerSide({ purge: true });
    }
  }, [activeTab, gridApi]);

  // Helper functions for KPI calculations
  const fmt = (num) => Number(num).toLocaleString("en-US");
  const getRowByType = (rows, type) => rows.find((r) => r?.[0] === type) ?? [];
  const getValue = (row, idx) => Number(row?.[idx] ?? 0);

  // Fetch KPI data
  const fetchKpiData = async () => {
    if (!startDate || !endDate) return;

    setKpiLoading(true);
    try {
      const start = startDate.toLocaleDateString("fr-CA");
      const end = endDate ? new Date(new Date(endDate).setDate(endDate.getDate() + 1)).toLocaleDateString("fr-CA") : "";

      const encryptedRole = handleEncryptValue(clientDetails.role);
      const encryptedClientId = handleEncryptValue(clientDetails.clientId);
      const encryptedProductCode = handleEncryptValue(productCode);
      const encryptedRegulation = handleEncryptValue(regulationCode);
      const encryptedStartDate = handleEncryptValue(start);
      const encryptedEndDate = handleEncryptValue(end);
      const encryptedOperation = handleEncryptValue("grid_kpi");

      const data = {
        client_id: encryptedClientId,
        role: encryptedRole,
        product: encryptedProductCode,
        regulation: encryptedRegulation,
        email_id: clientDetails.email,
        start_date: encryptedStartDate,
        end_date: encryptedEndDate,
        operations: encryptedOperation,
      };

      const regdataData = handleAPIEncryptValue(JSON.stringify(data));
      const url = getUrl("get-dashboard");

      const response = await get(`${url}?regdata=${regdataData}`, true);
      const { data: responseData } = response;

      if (responseData.statusCode === 200) {
        const iv = responseData.authData;
        const idToken = localStorage.getItem("userToken");
        const secretKey = idToken.slice(998, 1030);
        const body = handleDecryptResponse(
          responseData.encryptBody.toString(),
          secretKey.toString(),
          iv.toString(),
        );

        const kpiSource = body?.regdata_kpi_data?.P004;
        const rows = kpiSource?.data ?? [];
        const columns = kpiSource?.columns ?? [];

        if (!rows.length || !columns.length) {
          setKpiData(emptyKpis);
          return;
        }

        const validIdx = columns.indexOf("Valid Record");
        const invalidIdx = columns.indexOf("Invalid Record");
        const excludedIdx = columns.indexOf("Excluded Records");

        const orderRow = getRowByType(rows, "Order");
        const executionRow = getRowByType(rows, "Execution");

        const validOrds = getValue(orderRow, validIdx);
        const invalidOrds = getValue(orderRow, invalidIdx);
        const excludedOrds = getValue(orderRow, excludedIdx);

        const validExe = getValue(executionRow, validIdx);
        const invalidExe = getValue(executionRow, invalidIdx);
        const excludedExe = getValue(executionRow, excludedIdx);

        const valid = validOrds + validExe;
        const invalid = invalidOrds + invalidExe;
        const excluded = excludedOrds + excludedExe;

        const totalOrds = validOrds + invalidOrds + excludedOrds;
        const totalExe = validExe + invalidExe + excludedExe;
        const totalRecs = valid + invalid + excluded;

        const processable = valid + invalid;

        const validPctOfTotal = totalRecs ? (valid / totalRecs) * 100 : 0;
        const invalidPctOfTotal = totalRecs ? (invalid / totalRecs) * 100 : 0;
        const excludedPctOfTotal = totalRecs ? (excluded / totalRecs) * 100 : 0;

        const accuracyPct = processable ? (valid / processable) * 100 : 0;
        const accuracyOrdsPct = totalOrds ? (validOrds / totalOrds) * 100 : 0;
        const accuracyExePct = totalExe ? (validExe / totalExe) * 100 : 0;

        const errorPct = processable ? (invalid / processable) * 100 : 0;
        const errorOrdsPct = totalOrds ? (invalidOrds / totalOrds) * 100 : 0;
        const errorExePct = totalExe ? (invalidExe / totalExe) * 100 : 0;

        setKpiData({
          totalRecs,
          totalOrds,
          totalExe,
          valid,
          validOrds,
          validExe,
          validPctOfTotal,
          invalid,
          invalidOrds,
          invalidExe,
          invalidPctOfTotal,
          excluded,
          excludedOrds,
          excludedExe,
          excludedPctOfTotal,
          accuracyPct,
          accuracyOrdsPct,
          accuracyExePct,
          errorPct,
          errorOrdsPct,
          errorExePct,
        });
      }
    } catch (error) {
      console.error("Error fetching KPI data:", error);
      setKpiData(emptyKpis);
    } finally {
      setKpiLoading(false);
    }
  };

  // KPI Value wrapper component
  const KpiValue = ({ children, className = "text-start" }) => {
    if (kpiLoading) {
      return (
        <div className={className}>
          <CircularProgress className={className} size={16} />
        </div>
      );
    }
    return <>{children}</>;
  };
  // const handleDateRangeChanged = (dates) => {
  // const [start, end] = dates;
  // console.log("dates :", dates);

  // if (start && end) {
  //   const diffTime = Math.abs(end - start);
  //   const diffDays = diffTime / (1000 * 60 * 60 * 24);

  //   if (diffDays > 7) {
  //     toast.error("Maximum 7 days allowed");
  //     return;
  //   }
  // }

  // Update state only if valid
  // setStartDate(start);
  // setEndDate(end);
  // };

  const onCellMouseOver = (params) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const e = params.event;
      setHoverPos({ x: e.clientX, y: e.clientY });
      setShowDialog(true);
    }, 200);
  };

  const onCellMouseOut = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setShowDialog(false);
  };
  const handleDoubleClick = (params) => {
    setIsDetailBoxOpen(true);
    const clickedRow = params.data;
    if (clickedRow && Object.keys(clickedRow).length) {
      const isFieldIncludeUnderscore = Object.keys(clickedRow).some((field) =>
        field.includes("_"),
      );
      if (isFieldIncludeUnderscore) {
        let formattedData = {};
        params.api.getColumnDefs().forEach((col) => {
          formattedData[col.headerName] = clickedRow[col.field];
        });
        setSelectedRow(formattedData);
      } else {
        setSelectedRow(clickedRow);
      }
    }
  };
  const gridKey = useMemo(() => {
    const sd = startDate?.toISOString().slice(0, 10) ?? "";
    const ed = endDate?.toISOString().slice(0, 10) ?? "";
    // console.log(sd);
    // console.log(ed);
    return `${sd}_${ed}_${activeTab}_${refreshCounter}`;
  }, [startDate, endDate, refreshCounter, activeTab]);

  const handleRefresh = useCallback(() => {
    // force a full unmount/remount:
    setRefreshCounter((count) => count + 1);
  }, [gridApi]);

  // 1) Small util
  const toYMD = (val) => {
    if (!val) return val;
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return val; // if it's already a string, leave it
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };
  // --------------------------------------------------------------------------
  // 1. Asynchronous filter values provider
  // --------------------------------------------------------------------------
  const getAsyncFilterValues = (params) => {
    const filterModel = (gridApi && gridApi.getFilterModel()) || {};
    // Create a shallow copy and remove the current field.
    const activeFilters = { ...filterModel };
    const currentField = params.colDef.field;
    delete activeFilters[currentField];
    // console.log("startDate ", startDate);
    const s = startDate?.toLocaleDateString("fr-CA") ?? "";
    const e = endDate ? new Date(new Date(endDate).setDate(endDate.getDate() + 1)).toLocaleDateString("fr-CA") : "";
    // console.log(activeFilters?.["order_date_regx"]?.values);

    const activeFiltersArr = activeFilters?.["order_date_regx"]?.values.map(
      (v) => {
        const date = new Date(v);
        const formattedDate = date.toISOString().split("T")[0]; // → "2025-06-03"
        // console.log("formattedDate:", formattedDate);
        return formattedDate;
      },
    );
    let activeFiltersFinal = {};
    if (Object.keys(activeFilters).length !== 0) {
      activeFiltersFinal = {
        ...activeFilters,
        // order_date_regx: {
        //   ...activeFilters.order_date_regx,
        //   values: activeFiltersArr,
        // },
      };
    }
    // console.log("activeFilters", activeFilters);
    // console.log("activeFiltersFinal", activeFiltersFinal);

    const queryParams = new URLSearchParams({
      client_id: clientDetails.clientId,
      product: productCode,
      regulation: regulationCode,
      filters: JSON.stringify(activeFiltersFinal),
      email: clientDetails.email,
      datefiltercolumn: "order_date_regx",
      ...(s && { start_date: s }),
      ...(e && { end_date: e }),
      f_type: activeTabRef.current, // err || records
    });
    // for (const [key, value] of queryParams.entries()) {
    //   console.log(`${key}: ${value}`);
    // }
    const regDataGridData = handleAPIEncryptValue(queryParams.toString());
    const token = clientDetails.IdToken;
    fetch(`${filterUrl}?regdata=${regDataGridData}`, {
      method: "GET",
      mode: "cors",
      headers: {
        "x-api-key": clientDetails.REGDATA_FLASK_APP_ENCRYPTION_KEY,
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        // console.log("data  :", data);
        // Expect data to be an object mapping field names to arrays.
        params.success(data[currentField] || []);
      })
      .catch((error) => {
        console.error(
          `Error fetching filter values for ${currentField}:`,
          error,
        );
        params.success([]);
      });
  };
  // const getAsyncFilterValues = (params) => {
  //   const filterModel = (gridApi && gridApi.getFilterModel()) || {};
  //   // Create a shallow copy and remove the current field.
  //   const activeFilters = { ...filterModel };
  //   const currentField = params.colDef.field;
  //   delete activeFilters[currentField];
  //   const s = startDate?.toLocaleDateString("fr-CA") ?? "";
  //   const e = endDate?.toLocaleDateString("fr-CA") ?? "";
  //   const queryParams = new URLSearchParams({
  //     client_id: clientDetails.clientId,
  //     product: productCode,
  //     regulation: regulationCode,
  //     filters: JSON.stringify(activeFilters),
  //     email: clientDetails.email,
  //     // f_type: fTypeRef.current,
  //     ...(s && { start_date: s }),
  //     ...(e && { end_date: e }),
  //     datefiltercolumn: "order_date_regx",
  //   });

  //   for (const [key, value] of queryParams.entries()) {
  //     console.log(`${key}: ${value}`);
  //   }
  //   const regDataGridData = handleAPIEncryptValue(queryParams.toString());
  //   const token = localStorage.getItem("userToken");
  //   fetch(`${filterUrl}?regdata=${regDataGridData}`, {
  //     method: "GET",
  //     mode: "cors",
  //     headers: {
  //       "x-api-key": clientDetails.REGDATA_FLASK_APP_ENCRYPTION_KEY,
  //       Authorization: `Bearer ${token}`,
  //       "Content-Type": "application/json",
  //     },
  //   })
  //     .then((response) => response.json())
  //     .then((data) => {
  //       // Expect data to be an object mapping field names to arrays.
  //       params.success(data[currentField] || []);
  //     })
  //     .catch((error) => {
  //       console.error(
  //         `Error fetching filter values for ${currentField}:`,
  //         error
  //       );
  //       params.success([]);
  //     });
  // };

  // Formatter function for the Creation Date column
  const dateFormatter = (params) => {
    if (!params.value) return "";
    const date = new Date(params.value);
    return date.toISOString().split("T")[0]; // Returns YYYY-MM-DD
  };
  const handleClearFilters = useCallback(() => {
    if (!gridApi) return;

    // clear all column filters
    gridApi.setFilterModel(null);

    // clear sorting (optional)
    gridApi.setSortModel?.(null);

    // clear quick filter if you ever use it (safe to keep)
    gridApi.setGridOption?.("quickFilterText", "");
    gridApi.setQuickFilter?.("");

    // refresh SSRM
    gridApi.refreshServerSide({ purge: true });

    toast.success("All filters cleared");
  }, [gridApi]);

  const runAutosize = useCallback(() => {
    if (!gridApi) return;

    // ensures grid calculates with correct width
    gridApi.doLayout?.();

    requestAnimationFrame(() => {
      try {
        const colIds = gridApi
          .getAllDisplayedColumns()
          .map((c) => c.getColId());
        gridApi.autoSizeColumns(colIds, false);
      } catch (e) {
        gridApi.autoSizeAllColumns?.();
      }
    });
  }, [gridApi]);

  useEffect(() => {
    if (!isActive) return;

    // small delay helps because tab switches use display:none -> block
    const t = setTimeout(() => {
      runAutosize();
    }, 80);

    return () => clearTimeout(t);
  }, [isActive, runAutosize]);
  // --------------------------------------------------------------------------
  // 2. Column definitions
  // --------------------------------------------------------------------------
  const defaultColDef = useMemo(
    () => ({
      // flex: 1,
      resizable: true,
      sortable: true,
      cellStyle: { textAlign: "left" },
      floatingFilter: false,
      suppressMenu: false, // we need the column menu open for pivot
      enableValue: true,
      enablePivot: true,
      enableRowGroup: true, // drag here to pivot
    }),
    [],
  );

  const recordsColDefs = useMemo(
    () => [
      {
        field: "record_type_regx",
        headerName: "Record Type",
        filter: "agSetColumnFilter",
        filterParams: {
          values: getAsyncFilterValues,
          buttons: ["apply", "cancel"],
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "source_system_regx",
        headerName: "Source System",
        filter: "agSetColumnFilter",
        filterParams: {
          values: getAsyncFilterValues,
          buttons: ["apply", "cancel"],
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "cfi_code_regx",
        headerName: "CFI Code",
        filter: "agSetColumnFilter",
        filterParams: {
          values: getAsyncFilterValues,
          buttons: ["apply", "cancel"],
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "product_regx",
        headerName: "Product",
        filter: "agSetColumnFilter",
        filterParams: {
          values: getAsyncFilterValues,
          buttons: ["apply", "cancel"],
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "order_date_regx",
        headerName: "Trade Date",
        filter: "agSetColumnFilter",
        valueFormatter: (params) => {
          const raw = params.value;
          if (!raw) return "";
          const d = new Date(raw);
          if (isNaN(d)) return raw;
          // console.log("raw  :", d.toISOString().split("T")[0]);
          return d.toISOString().split("T")[0];
        },
        filterParams: {
          values: getAsyncFilterValues,
          keyCreator: (p) => toYMD(p.value),
          valueFormatter: (p) => toYMD(p.value),
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
          treeList: true,
          treeListPathGetter: (rawDateValue) => {
            // console.log("rawDateValue :", rawDateValue);
            if (!rawDateValue) return ["Unknown"];
            const d = new Date(rawDateValue);
            if (isNaN(d)) return [rawDateValue];
            // console.log("d  :", d?.toLocaleDateString("fr-CA"));
            const year = String(d.getFullYear());
            const monthName = d.toLocaleString("en-US", { month: "short" });
            const day = String(d.getDate()).padStart(2, "0");
            // return d?.toLocaleDateString("fr-CA");
            return [year, monthName, day];
          },
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "entity_id",
        headerName: "Identification of the entity which submitted the order",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "dea",
        headerName: "Direct Electronic Access (DEA)",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "client_id_code",
        headerName: "Client identification code",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "investment_decision",
        headerName: "Investment decision within firm",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "execution_within_firm",
        headerName: "Execution within firm",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "non_executing_broker",
        headerName: "Non-executing broker",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "trading_capacity",
        headerName: "Trading capacity",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "liquidity_provision",
        headerName: "Liquidity provision activity",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "order_datetime",
        headerName: "Date and Time",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "validity_period",
        headerName: "Validity period",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "order_restriction",
        headerName: "Order restriction",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "validity_period_time",
        headerName: "Validity period and time",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "priority_timestamp",
        headerName: "Priority time stamp",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "priority_size",
        headerName: "Priority size",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "sequence_number",
        headerName: "Sequence number",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "segment_mic_code",
        headerName: "Segment MIC code",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "order_book_code",
        headerName: "Order book code",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "instrument_code",
        headerName: "Financial instrument identification code",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "date_of_receipt",
        headerName: "Date of receipt",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "order_id_code",
        headerName: "Order identification code",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "order_event_type",
        headerName:
          "New order, order modification, order cancellation, order rejections, partial or full execution.",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "order_type",
        headerName: "Order type",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "order_type_classification",
        headerName: "Order type classification",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "limit_price",
        headerName: "Limit price",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "additional_limit_price",
        headerName: "Additional limit Price",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "stop_price",
        headerName: "Stop price",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "pegged_limit_price",
        headerName: "Pegged limit price",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "transaction_price",
        headerName: "Transaction price",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "price_currency",
        headerName: "Price currency",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "leg2_currency",
        headerName: "Currency of leg 2",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "price_notation",
        headerName: "Price notation",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "buy_sell_indicator",
        headerName: "Buy-sell indicator",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "order_status",
        headerName: "Order status",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "quantity_notation",
        headerName: "Quantity notation",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "quantity_currency",
        headerName: "Quantity currency",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "initial_quantity",
        headerName: "Initial quantity",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "remaining_quantity",
        headerName: "Remaining quantity including hidden.",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "displayed_quantity",
        headerName: "Displayed quantity",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "traded_quantity",
        headerName: "Traded quantity",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "maq",
        headerName: "Minimum Acceptable Quantity (MAQ)",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "mes",
        headerName: "Minimum executable size (MES)",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "mes_first_execution_only",
        headerName: "MES first execution only",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "passive_only",
        headerName: "Passive only indicator",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "passive_aggressive_indicator",
        headerName: "Passive or aggressive indicator",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "self_execution_prevention",
        headerName: "Self-Execution Prevention",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "strategy_linked_order_id",
        headerName: "Strategy Linked Order identification",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "routing_strategy",
        headerName: "Routing Strategy",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "trading_venue_transaction_id",
        headerName: "Trading venue transaction identification code",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "trading_phases",
        headerName: "Trading phases",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "indicative_auction_price",
        headerName: "Indicative auction price",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "indicative_auction_volume",
        headerName: "Indicative auction volume",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
    ],
    [clientDetails, productCode, regulationCode, gridApi],
  );

  const errorColDefs = useMemo(
    () => [
      {
        field: "record_type_regx",
        headerName: "Record Type",
        filter: "agSetColumnFilter",
        filterParams: {
          values: getAsyncFilterValues,
          buttons: ["apply", "cancel"],
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "source_system_regx",
        headerName: "Source System",
        filter: "agSetColumnFilter",
        filterParams: {
          values: getAsyncFilterValues,
          buttons: ["apply", "cancel"],
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "cfi_code_regx",
        headerName: "CFI Code",
        filter: "agSetColumnFilter",
        filterParams: {
          values: getAsyncFilterValues,
          buttons: ["apply", "cancel"],
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "product_regx",
        headerName: "Product",
        filter: "agSetColumnFilter",
        filterParams: {
          values: getAsyncFilterValues,
          buttons: ["apply", "cancel"],
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      // added on 23-Jan
      {
        field: "error_description",
        headerName: "Error Details",
        filter: "agSetColumnFilter",
        filterParams: {
          values: getAsyncFilterValues,
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "input_file_column",
        headerName: "Input File Column",
        filter: "agSetColumnFilter",
        filterParams: {
          values: getAsyncFilterValues,
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      // ---
      {
        field: "order_date_regx",
        headerName: "Trade Date",
        filter: "agSetColumnFilter",
        valueFormatter: (params) => {
          const raw = params.value;
          if (!raw) return "";
          const d = new Date(raw);
          if (isNaN(d)) return raw;
          // console.log("raw  :", d.toISOString().split("T")[0]);
          return d.toISOString().split("T")[0];
        },
        filterParams: {
          values: getAsyncFilterValues,
          keyCreator: (p) => toYMD(p.value),
          valueFormatter: (p) => toYMD(p.value),
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
          treeList: true,
          treeListPathGetter: (rawDateValue) => {
            // console.log("rawDateValue :", rawDateValue);
            if (!rawDateValue) return ["Unknown"];
            const d = new Date(rawDateValue);
            if (isNaN(d)) return [rawDateValue];
            // console.log("d  :", d?.toLocaleDateString("fr-CA"));
            const year = String(d.getFullYear());
            const monthName = d.toLocaleString("en-US", { month: "short" });
            const day = String(d.getDate()).padStart(2, "0");
            // return d?.toLocaleDateString("fr-CA");
            return [year, monthName, day];
          },
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "entity_id",
        headerName: "Identification of the entity which submitted the order",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "dea",
        headerName: "Direct Electronic Access (DEA)",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "client_id_code",
        headerName: "Client identification code",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "investment_decision",
        headerName: "Investment decision within firm",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "execution_within_firm",
        headerName: "Execution within firm",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "non_executing_broker",
        headerName: "Non-executing broker",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "trading_capacity",
        headerName: "Trading capacity",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "liquidity_provision",
        headerName: "Liquidity provision activity",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "order_datetime",
        headerName: "Date and Time",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "validity_period",
        headerName: "Validity period",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "order_restriction",
        headerName: "Order restriction",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "validity_period_time",
        headerName: "Validity period and time",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "priority_timestamp",
        headerName: "Priority time stamp",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "priority_size",
        headerName: "Priority size",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "sequence_number",
        headerName: "Sequence number",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "segment_mic_code",
        headerName: "Segment MIC code",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "order_book_code",
        headerName: "Order book code",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "instrument_code",
        headerName: "Financial instrument identification code",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "date_of_receipt",
        headerName: "Date of receipt",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "order_id_code",
        headerName: "Order identification code",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "order_event_type",
        headerName:
          "New order, order modification, order cancellation, order rejections, partial or full execution.",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "order_type",
        headerName: "Order type",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "order_type_classification",
        headerName: "Order type classification",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "limit_price",
        headerName: "Limit price",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "additional_limit_price",
        headerName: "Additional limit Price",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "stop_price",
        headerName: "Stop price",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "pegged_limit_price",
        headerName: "Pegged limit price",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "transaction_price",
        headerName: "Transaction price",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "price_currency",
        headerName: "Price currency",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "leg2_currency",
        headerName: "Currency of leg 2",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "price_notation",
        headerName: "Price notation",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "buy_sell_indicator",
        headerName: "Buy-sell indicator",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "order_status",
        headerName: "Order status",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "quantity_notation",
        headerName: "Quantity notation",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "quantity_currency",
        headerName: "Quantity currency",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "initial_quantity",
        headerName: "Initial quantity",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "remaining_quantity",
        headerName: "Remaining quantity including hidden.",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "displayed_quantity",
        headerName: "Displayed quantity",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "traded_quantity",
        headerName: "Traded quantity",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "maq",
        headerName: "Minimum Acceptable Quantity (MAQ)",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "mes",
        headerName: "Minimum executable size (MES)",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "mes_first_execution_only",
        headerName: "MES first execution only",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "passive_only",
        headerName: "Passive only indicator",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "passive_aggressive_indicator",
        headerName: "Passive or aggressive indicator",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "self_execution_prevention",
        headerName: "Self-Execution Prevention",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "strategy_linked_order_id",
        headerName: "Strategy Linked Order identification",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "routing_strategy",
        headerName: "Routing Strategy",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "trading_venue_transaction_id",
        headerName: "Trading venue transaction identification code",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "trading_phases",
        headerName: "Trading phases",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "indicative_auction_price",
        headerName: "Indicative auction price",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "indicative_auction_volume",
        headerName: "Indicative auction volume",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
    ],
    [clientDetails, productCode, regulationCode, gridApi],
  );

  const activeColDefs = useMemo(() => {
    if (activeTab === "err") return errorColDefs;
    return recordsColDefs;
  }, [activeTab]);

  // --------------------------------------------------------------------------
  // 3. Server-side datasource: fetch row data using the grid’s filter model.
  // --------------------------------------------------------------------------
  const onGridReady = useCallback(
    (params) => {
      setIsLoading(true);
      setGridApi(params.api);
      params.api.closeToolPanel();
      // console.log(gridApi);
      const datasource = {
        getRows: async (params) => {
          // console.log(clientDetails);
          // console.log(params);
          const {
            startRow,
            endRow,
            filterModel,
            sortModel,
            pivotMode,
            pivotCols, // ← AG Grid’s list of pivot columns
            valueCols, // ← AG Grid’s list of value columns
            rowGroupCols,
          } = params.request;

          // const limit = params.api.paginationGetPageSize();
          // const page = Math.floor(startRow / limit) + 1;

          // Process filters into the format your API expects.
          if (!startDate || !endDate) {
            // If either date is missing, skip the check
            return;
          }

          // const diffTime = Math.abs(startDate - endDate);
          // const diffDays = diffTime / (1000 * 60 * 60 * 24);
          // console.log("diffDays :", diffDays);
          // if (diffDays > 7) {
          //   toast.error("Maximum 7 days allowed");
          //   params.fail(); // Stop AG Grid request
          //   return;
          // }
          const s = startDate?.toLocaleDateString("fr-CA") ?? "";
          const e = endDate ? new Date(new Date(endDate).setDate(endDate.getDate() + 1)).toLocaleDateString("fr-CA") : "";
          // console.log("s and e :", s, e);
          const filters = {};
          Object.keys(filterModel).forEach((colId) => {
            const filterObj = filterModel[colId];
            if (filterObj.values) {
              filters[colId] = {
                type: filterObj.filterType,
                values: filterObj.values,
              };
            } else if (filterObj.conditions) {
              filters[colId] = {
                operator: filterObj.operator,
                conditions: filterObj.conditions.map((cond) => ({
                  type: cond.type,
                  value: cond.filter,
                })),
              };
            } else {
              filters[colId] = {
                type: filterObj.type,
                value: filterObj.filter,
              };
            }
          });
          let sort_by = "";
          let order = "";
          if (sortModel && sortModel.length > 0) {
            sort_by = sortModel[0].colId;
            order = sortModel[0].sort === "asc" ? "ascending" : "descending";
          }

          const queryParams = new URLSearchParams({
            startRow,
            endRow,
            client_id: clientDetails.clientId,
            product: productCode,
            regulation: regulationCode,
            filters: JSON.stringify(filters),
            fType: activeTab,
            ...(s && { start_date: s }),
            ...(e && { end_date: e }),
            email: clientDetails.email,
            f_type: activeTabRef.current, // err || records
            datefiltercolumn: "order_date_regx",
          });

          // Append sort parameters if sorting is applied.
          if (sort_by) {
            queryParams.append("sort_by", sort_by);
            queryParams.append("order", order);
          }
          // for (const [key, value] of queryParams.entries()) {
          //   console.log(`${key}: ${value}`);
          // }

          queryParams.append("pivotMode", pivotMode);

          const pivotFields = pivotCols.map((p) => p.field).join(","); // Columns
          const groupFields = rowGroupCols.map((r) => r.field).join(","); // Rows
          const aggField = valueCols[0]?.field || "";
          const aggFunc = valueCols[0]?.aggFunc || "";

          queryParams.append("pivotCols", pivotFields); // can be empty
          queryParams.append("rowgroupCols", groupFields); // can be empty
          queryParams.append("aggCol", aggField); // can be empty
          queryParams.append("aggFunc", aggFunc); // can be empty

          //     for (const [key, value] of queryParams.entries()) {
          //   console.log(`${key}: ${value}`);
          // }
          const regDataGridData = handleAPIEncryptValue(queryParams.toString());
          // console.log(regDataGridData);
          try {
            const gridUrl = getUrl("get-flask-data");
            const token = clientDetails.IdToken;
            const response = await fetch(
              `${gridUrl}?regdata=${regDataGridData}`,
              {
                method: "GET",
                mode: "cors",
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                  "x-api-key": clientDetails.REGDATA_FLASK_APP_ENCRYPTION_KEY,
                },
              },
            );
            // console.log(response, "--response");
            const statusCode = response.status;
            if (statusCode === 403) {
              toast.error("Session expired. Please login again");
              tokenExpire(statusCode, navigate);
              return;
            }
            if (!response.ok) {
              throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const data = await response.json();
            if (data && data.rowData) {
              setTotalRowCount(data.rowCount);

              const min = 1;
              const max = data.rowCount > 500000 ? data.rowCount : 500000;

              setRowRangeLimits({ min, max });
              setRowRange([min, max]);
              // console.log("data.rowData :", data.rowData);
              if (data.rowData.length === 0) {
                params.success({
                  rowCount: 0,
                  rowData: [],
                });
                params.api.showNoRowsOverlay();
              } else {
                params.api.hideOverlay();
                params.success({
                  rowData: data.rowData,
                  rowCount: data.rowCount,
                  ...(pivotMode && data.pivotFields?.length
                    ? { pivotResultFields: data.pivotFields }
                    : {}),
                });
              }
            } else {
              params.fail();
            }
          } catch (error) {
            console.error("Error fetching data:", error);
            console.log("erorr :", error);
            params.fail();
          } finally {
            setIsLoading(false);
          }
        },
      };
      params.api.setGridOption("serverSideDatasource", datasource);
      setTimeout(() => {
        params.api.autoSizeAllColumns();
      }, 200);
    },
    [clientDetails, productCode, regulationCode, startDate, endDate, activeTab],
  );

  // --------------------------------------------------------------------------
  // 4. onFilterChanged: Refresh asynchronous filter values for the columns using set filters
  // --------------------------------------------------------------------------
  const onFilterChanged = useCallback(() => {
    if (filterChangedTimeout.current) {
      clearTimeout(filterChangedTimeout.current);
    }
    filterChangedTimeout.current = setTimeout(() => {
      if (!gridApi) return;
      const currentModel = gridApi.getFilterModel();
      const colDefs = gridApi.getColumnDefs() || [];
      colDefs.forEach((colDef) => {
        if (colDef.filter === "agSetColumnFilter") {
          // Determine if the filter for this column is active.
          const isActive = !!currentModel[colDef.field];
          // Only refresh if the column was inactive previously and remains inactive.
          if (!isActive && !prevAsyncActiveState.current[colDef.field]) {
            gridApi
              .getColumnFilterInstance(colDef.field)
              .then((filterInstance) => {
                if (
                  filterInstance &&
                  typeof filterInstance.refreshFilterValues === "function"
                ) {
                  try {
                    filterInstance.refreshFilterValues();
                  } catch (e) {
                    console.warn(
                      `Refresh error for ${colDef.field}: ${e.message}`,
                    );
                  }
                }
              });
          }
          // Update the previous active state.
          prevAsyncActiveState.current[colDef.field] = isActive;
        }
      });
    }, 300);
  }, [gridApi]);

  // --------------------------------------------------------------------------
  // 5. CSV export – downloads the CSV based on the current filter model.
  // --------------------------------------------------------------------------
  const downloadCSV = async () => {
    if (!gridApi) {
      console.error("gridApi is not available");
      return;
    }

    const MAX_ROWS = 500000;
    // console.log(totalRowCount, '=TotalRC');

    if (totalRowCount > MAX_ROWS) {
      setShowContactSupport(true);
      setShowSupportDialog(true); // 👈 open the dialog instead of alert
      return;
    }

    setIsDownloading(true);
    const selectedColumns = gridApi
      .getAllDisplayedColumns()
      .map((col) => col.getColId());
    const filterModel = gridApi.getFilterModel();
    const token = clientDetails.IdToken;
    const s = startDate?.toLocaleDateString("fr-CA") ?? "";
    const e = endDate ? new Date(new Date(endDate).setDate(endDate.getDate() + 1)).toLocaleDateString("fr-CA") : "";
    const queryParams = new URLSearchParams({
      client_id: clientDetails.clientId,
      product: productCode,
      regulation: regulationCode,
      email: clientDetails.email,
      ...(s && { start_date: s }),
      ...(e && { end_date: e }),
      f_type: activeTabRef.current, // err || records
      datefiltercolumn: "order_date_regx",
    });
    // for (const [key, value] of queryParams.entries()) {
    //   console.log(`${key}: ${value}`);
    // }
    queryParams.append("filters", JSON.stringify(filterModel));
    queryParams.append("columns", JSON.stringify(selectedColumns));
    // console.log("selectedColumns :", selectedColumns);
    // console.log("filterModel :", filterModel);
    const regDataGridData = handleAPIEncryptValue(queryParams.toString());

    try {
      const response = await fetch(
        `${downloadUrl}?regdata=${regDataGridData}`,
        {
          method: "GET",
          mode: "cors",
          headers: {
            "x-api-key": clientDetails.REGDATA_FLASK_APP_ENCRYPTION_KEY,
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.statusText}`);
      }

      const blob = await response.blob();
      const now = new Date();
      const pad = (num) => String(num).padStart(2, "0");
      const fileName = `rts24_${now.getFullYear()}${pad(
        now.getMonth() + 1,
      )}${pad(now.getDate())}_${pad(now.getHours())}${pad(
        now.getMinutes(),
      )}${pad(now.getSeconds())}.csv`;

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);
    } catch (error) {
      console.error("Error downloading CSV:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleContactSupport = async () => {
    if (!gridApi) return;

    const filterModel = gridApi.getFilterModel();
    const selectedColumns = gridApi
      .getAllDisplayedColumns()
      .map((col) => col.getColId());

    const payload = {
      client_id: clientDetails.clientId,
      product: productCode,
      regulation: regulationCode,
      email: clientDetails.email,
      filters: filterModel,
      columns: selectedColumns,
      rowCount: totalRowCount,
      requestedRange: { min: rowRange[0], max: rowRange[1] },
      timestamp: new Date().toISOString(),
    };

    const encryptedPayload = handleAPIEncryptValue(JSON.stringify(payload));
    const supportUrl = getUrl("contact-support"); // 👈 Your API key name here

    try {
      const response = await fetch(`${supportUrl}`, {
        method: "POST",
        headers: {
          "x-api-key": clientDetails.REGDATA_FLASK_APP_ENCRYPTION_KEY,
          Authorization: `Bearer ${clientDetails.IdToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ regdata: payload }),
      });

      if (!response.ok) {
        throw new Error(`Request failed: ${response.statusText}`);
      }

      alert("Support request sent successfully!");
      setShowContactSupport(false); // Optional: hide the support message again
    } catch (error) {
      console.error("Failed to send support request:", error);
      alert(
        "Something went wrong while sending the request. Please try again later.",
      );
    }
  };

  // --------------------------------------------------------------------------
  // 6. Check regulation accessibility based on client details.
  // --------------------------------------------------------------------------

  useEffect(() => {
    if (gridApi && isActive) {
      gridApi.autoSizeAllColumns();
    }
  }, [gridApi, isActive]);

  const onFirstDataRendered = useCallback(
    (params) => {
      if (!isActive) return;
      params.api.autoSizeAllColumns();
    },
    [isActive],
  );

  const onModelUpdated = useCallback(
    (params) => {
      if (!isActive) return;
      params.api.autoSizeAllColumns();
    },
    [isActive],
  );
  // Fetch KPI data when component mounts or dates change
  useEffect(() => {
    if (isRegulationAccessible && startDate && endDate && isActive) {
      fetchKpiData();
    }
  }, [startDate, endDate, isRegulationAccessible, isActive]);

  useEffect(() => {
    if (
      productCode in clientDetails.dataAccess &&
      clientDetails.dataAccess.P001?.includes(regulationCode)
    ) {
      setIsRegulationAccessible(true);
    } else {
      setIsRegulationAccessible(false);
    }
  }, [regulationCode, clientDetails, productCode]);

  return (
    <main
      className={`${isFullscreen ? "" : "middle_section"} dashboard_middle`}
    >
      <div className="container-fluid">
        {/* KPIs Section */}

        {isRegulationAccessible ? (
          <>
            <div className="grid-kpi-wrapper">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="col-auto px-0">
                  <CustomDatepicker
                    startDate={startDate}
                    endDate={endDate}
                    onChange={(newDateRange) => {
                      setDateRange(newDateRange);
                      if (newDateRange[0] && newDateRange[1]) {
                        setTimeout(() => fetchKpiData(), 100);
                      }
                    }}
                    showWrapperStyles={false}
                  />
                </div>
                <GridGroupIcons
                  onRefresh={() => {
                    handleRefresh();
                    fetchKpiData();
                  }}
                  fullScreenIcon={false}
                  iconsDisabled={isLoading || kpiLoading}
                  downloadIcon={false}
                  // borderRadius={10}
                />
              </div>
              <div className="kpis">
                {/* TOTAL */}
                <div className="kpi violet">
                  <div
                    style={{
                      display: "grid",
                      gap: "10px",
                      alignItems: "flex-start",
                      gridTemplateColumns: "40% auto",
                    }}
                  >
                    <div>
                      <div className="kpi-title">Total Records</div>
                      <div className="kpi-value">
                        <KpiValue>{fmt(kpiData.totalRecs)}</KpiValue>
                      </div>
                    </div>
                    <KpiValue className="text-center">
                      <div className="kpi-breakdown kpi-breakdown-total">
                        <div className="breakdown-item">
                          <span className="breakdown-label">Orders:</span>
                          <span className="breakdown-value">
                            {fmt(kpiData.totalOrds)}
                          </span>
                        </div>
                        <div className="breakdown-item">
                          <span className="breakdown-label">Executions:</span>
                          <span className="breakdown-value">
                            {fmt(kpiData.totalExe)}
                          </span>
                        </div>
                      </div>
                    </KpiValue>
                  </div>
                  <div className="kpi-foot">
                    in current view
                    <span onClick={() => setActiveKpiKey("totalRecords")}>
                      <FaInfoCircle className="kpi-info-icon" />
                    </span>
                  </div>
                </div>

                {/* VALID */}
                <div className="kpi green">
                  <div
                    style={{
                      display: "grid",
                      gap: "10px",
                      alignItems: "flex-start",
                      gridTemplateColumns: "50% auto",
                    }}
                  >
                    <div>
                      <div className="kpi-title">Processed Records</div>
                      <div className="kpi-value">
                        <KpiValue>{fmt(kpiData.valid)}</KpiValue>
                      </div>
                    </div>
                    <KpiValue className="text-center">
                      <div className="kpi-breakdown kpi-breakdown-valid">
                        <div className="breakdown-item">
                          <span className="breakdown-label">Orders:</span>
                          <span className="breakdown-value">
                            {fmt(kpiData.validOrds)}
                          </span>
                        </div>
                        <div className="breakdown-item">
                          <span className="breakdown-label">Executions:</span>
                          <span className="breakdown-value">
                            {fmt(kpiData.validExe)}
                          </span>
                        </div>
                      </div>
                    </KpiValue>
                  </div>
                  <div className="kpi-foot">
                    {kpiData.validPctOfTotal.toFixed(2)}% of total
                    <span onClick={() => setActiveKpiKey("processedRecords")}>
                      <FaInfoCircle className="kpi-info-icon" />
                    </span>
                  </div>
                </div>

                {/* INVALID */}
                <div className="kpi red">
                  <div
                    style={{
                      display: "grid",
                      gap: "10px",
                      alignItems: "flex-start",
                      gridTemplateColumns: "50% auto",
                    }}
                  >
                    <div>
                      <div className="kpi-title">Exceptions</div>
                      <div className="kpi-value">
                        <KpiValue>{fmt(kpiData.invalid)}</KpiValue>
                      </div>
                    </div>
                    <KpiValue className="text-center">
                      <div className="kpi-breakdown kpi-breakdown-invalid">
                        <div className="breakdown-item">
                          <span className="breakdown-label">Orders:</span>
                          <span className="breakdown-value">
                            {fmt(kpiData.invalidOrds)}
                          </span>
                        </div>
                        <div className="breakdown-item">
                          <span className="breakdown-label">Executions:</span>
                          <span className="breakdown-value">
                            {fmt(kpiData.invalidExe)}
                          </span>
                        </div>
                      </div>
                    </KpiValue>
                  </div>
                  <div className="kpi-foot">
                    {kpiData.invalidPctOfTotal.toFixed(2)}% of total
                    <span onClick={() => setActiveKpiKey("exceptionsRecords")}>
                      <FaInfoCircle className="kpi-info-icon" />
                    </span>
                  </div>
                </div>

                {/* EXCLUDED */}
                <div className="kpi amber">
                  <div
                    style={{
                      display: "grid",
                      gap: "10px",
                      alignItems: "flex-start",
                      gridTemplateColumns: "50% auto",
                    }}
                  >
                    <div>
                      <div className="kpi-title">Excluded Records</div>
                      <div className="kpi-value">
                        <KpiValue>{fmt(kpiData.excluded)}</KpiValue>
                      </div>
                    </div>
                    <KpiValue className="text-center">
                      <div className="kpi-breakdown kpi-breakdown-excluded">
                        <div className="breakdown-item">
                          <span className="breakdown-label">Orders:</span>
                          <span className="breakdown-value">
                            {fmt(kpiData.excludedOrds)}
                          </span>
                        </div>
                        <div className="breakdown-item">
                          <span className="breakdown-label">Executions:</span>
                          <span className="breakdown-value">
                            {fmt(kpiData.excludedExe)}
                          </span>
                        </div>
                      </div>
                    </KpiValue>
                  </div>
                  <div className="kpi-foot">
                    {kpiData.excludedPctOfTotal.toFixed(2)}% of total
                    <span onClick={() => setActiveKpiKey("excludedRecords")}>
                      <FaInfoCircle className="kpi-info-icon" />
                    </span>
                  </div>
                </div>

                {/* ACCURACY */}
                <div className="kpi green">
                  <div
                    style={{
                      display: "grid",
                      gap: "10px",
                      alignItems: "flex-start",
                      gridTemplateColumns: "50% auto",
                    }}
                  >
                    <div>
                      <div className="kpi-title">Accuracy %</div>
                      <div className="kpi-value">
                        <KpiValue>{kpiData.accuracyPct.toFixed(2)}%</KpiValue>
                      </div>
                    </div>
                    <KpiValue className="text-center">
                      <div className="kpi-breakdown kpi-breakdown-accuracy">
                        <div className="breakdown-item">
                          <span className="breakdown-label">Orders:</span>
                          <span className="breakdown-value">
                            {kpiData.accuracyOrdsPct.toFixed(2)}%
                          </span>
                        </div>
                        <div className="breakdown-item">
                          <span className="breakdown-label">Executions:</span>
                          <span className="breakdown-value">
                            {kpiData.accuracyExePct.toFixed(2)}%
                          </span>
                        </div>
                      </div>
                    </KpiValue>
                  </div>
                  <div className="kpi-foot">
                    <span onClick={() => setActiveKpiKey("accuracyPercentage")}>
                      <FaInfoCircle className="kpi-info-icon" />
                    </span>
                  </div>
                </div>

                {/* ERROR */}
                <div className="kpi red">
                  <div
                    style={{
                      display: "grid",
                      gap: "10px",
                      alignItems: "flex-start",
                      gridTemplateColumns: "50% auto",
                    }}
                  >
                    <div>
                      <div className="kpi-title">Error %</div>
                      <div className="kpi-value">
                        <KpiValue>{kpiData.errorPct.toFixed(2)}%</KpiValue>
                      </div>
                    </div>
                    <KpiValue className="text-center">
                      <div className="kpi-breakdown kpi-breakdown-error">
                        <div className="breakdown-item">
                          <span className="breakdown-label">Orders:</span>
                          <span className="breakdown-value">
                            {kpiData.errorOrdsPct.toFixed(2)}%
                          </span>
                        </div>
                        <div className="breakdown-item">
                          <span className="breakdown-label">Executions:</span>
                          <span className="breakdown-value">
                            {kpiData.errorExePct.toFixed(2)}%
                          </span>
                        </div>
                      </div>
                    </KpiValue>
                  </div>
                  <div className="kpi-foot">
                    <span onClick={() => setActiveKpiKey("errorPercentage")}>
                      <FaInfoCircle className="kpi-info-icon" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div
              className={`dashboard_inner pt-0 ${
                isFullscreen ? "fullscreen-grid" : "mt-3"
              }`}
            >
              <div
                className={`rx_table_wrapper table-responsive padding-fix ${
                  activeTab === "err" ? "red-grid" : ""
                }`}
              >
                <div className="d-flex justify-content-between align-items-center grid-icon-wrapper">
                  <DynamicTabSwitcher
                    onTabChange={handleTabChange}
                    tabMap={TAB_MAP}
                    activeTab={activeTab}
                    wrapperClassName="pt-0 pb-0"
                    disabledTabs={[]}
                  />
                  <div className="d-flex align-items-center gap-2">
                    <Tooltip
                      title="Clear applied filters"
                      arrow
                      placement="top"
                    >
                      <span>
                        <IconButton
                          size="small"
                          onClick={handleClearFilters}
                          disabled={isLoading || !gridApi}
                          sx={{
                            border: "1px solid #cbd5e1",
                            borderRadius: "8px",
                            height: 36,
                            width: 36,
                          }}
                        >
                          <FilterAltOffIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <GridGroupIcons
                      refreshIcon={false}
                      isFullscreen={isFullscreen}
                      iconsDisabled={isLoading || kpiLoading}
                      onToggleFullscreen={() => setIsFullscreen(!isFullscreen)}
                      onDownloadCSV={downloadCSV}
                      isDownloading={isDownloading}
                      // borderRadius={10}
                    />
                  </div>
                </div>
                <div
                  className={`container-fluid p-0 ${
                    activeTab === "err" ? "red-grid" : ""
                  }`}
                >
                  <div
                    className={` ag-theme-alpine ag-theme-quartz `}
                    style={{
                      height: isFullscreen ? "calc(100vh - 60px)" : "80vh",
                      width: "100%",
                    }}
                  >
                    <AgGridReact
                      ref={gridRef}
                      key={gridKey}
                      theme="legacy"
                      columnDefs={activeColDefs}
                      defaultColDef={defaultColDef}
                      rowModelType="serverSide"
                      pagination={true}
                      paginationPageSize={20}
                      paginationPageSizeSelector={[10, 20, 50, 100]}
                      cacheBlockSize={10}
                      onFirstDataRendered={onFirstDataRendered}
                      onModelUpdated={onModelUpdated}
                      onGridReady={onGridReady}
                      onFilterChanged={onFilterChanged}
                      onCellMouseOver={onCellMouseOver}
                      onCellMouseOut={onCellMouseOut}
                      onRowDoubleClicked={handleDoubleClick}
                      cellSelection={true}
                      enableCharts
                      // pivotMode={false}
                      // pivotPanelShow="always"
                      // sideBar="columns"
                      // serverSidePivotResultFieldSeparator="."
                      noRowsOverlayComponent={GridDataNotFound}
                    />
                  </div>
                </div>
              </div>
              {/* <div className="d-flex justify-content-end mt-4">
              <button
                className="dash_btn"
                type="submit"
                onClick={downloadCSV}
                disabled={isDownloading}
                style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}
              >
                {isDownloading ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Download CSV"}
              </button>
            </div> */}
              {/* {showContactSupport && (
              <div className="d-flex flex-column align-items-start mt-3">
                <p style={{ color: "red", fontSize: "0.9em" }}>
                  You've exceeded the 500000 row download limit. Please contact support for full data.
                </p>
                <button
                  className="dash_btn"
                  type="button"
                  onClick={handleContactSupport}
                  style={{ marginTop: "4px" }}
                >
                  Contact Support
                </button>
              </div>
            )} */}
            </div>
          </>
        ) : (
          <NotSubscribedMessageComponent />
        )}
      </div>
      <GridRowDetailsExpand
        showDialog={showDialog}
        selectedRow={selectedRow}
        hoverPos={hoverPos}
        isErrGrid={activeTab === "err"}
        isDetailBoxOpen={isDetailBoxOpen}
        setIsDetailBoxOpen={setIsDetailBoxOpen}
      />

      {/* ✅ Place your dialog here */}
      <Dialog
        sx={{ zIndex: 99999999 }}
        open={showSupportDialog}
        disableScrollLock
        onClose={() => setShowSupportDialog(false)}
        fullWidth
        maxWidth="md"
      >
        {showTable ? (
          <>
            <DialogTitle>Requested Files</DialogTitle>
            <DialogContent>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>File Name</TableCell>
                      <TableCell>Requested On</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      { name: "rts24_jan_data.csv", date: "2025-01-10" },
                      { name: "rts24_feb_data.csv", date: "2025-02-14" },
                      { name: "rts24_mar_data.csv", date: "2025-03-05" },
                      { name: "rts24_jan_data.csv", date: "2025-01-10" },
                      { name: "rts24_feb_data.csv", date: "2025-02-14" },
                      { name: "rts24_mar_data.csv", date: "2025-03-05" },
                      { name: "rts24_jan_data.csv", date: "2025-01-10" },
                      { name: "rts24_feb_data.csv", date: "2025-02-14" },
                      { name: "rts24_mar_data.csv", date: "2025-03-05" },
                      { name: "rts24_jan_data.csv", date: "2025-01-10" },
                      { name: "rts24_feb_data.csv", date: "2025-02-14" },
                      { name: "rts24_mar_data.csv", date: "2025-03-05" },
                      { name: "rts24_jan_data.csv", date: "2025-01-10" },
                      { name: "rts24_feb_data.csv", date: "2025-02-14" },
                      { name: "rts24_mar_data.csv", date: "2025-03-05" },
                    ].map((file, index) => (
                      <TableRow key={index}>
                        <TableCell>{file.name}</TableCell>
                        <TableCell>{file.date}</TableCell>
                        <TableCell>
                          <Button
                            variant="outlined"
                            size="small"
                            color="primary"
                            onClick={() =>
                              toast.info(`Downloading ${file.name}...`)
                            }
                          >
                            Download
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowTable(false)} color="primary">
                Back
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle fontSize={22} fontWeight={500}>
              Download Limit Exceeded
            </DialogTitle>
            <DialogContent>
              <Typography mb={2} fontSize={16}>
                Download limit is 5,00,000 rows. Please contact support for data
                more than 5,00,000 rows.
              </Typography>
              <Typography mb={0} fontSize={14}>
                Contact Us:{" "}
                <p style={{ color: "blue", display: "inline" }}>
                  {" "}
                  support.reg-x.co.uk{" "}
                </p>
              </Typography>
              {/* <Box sx={{ px: 2, pt: 1 }}>
                <Slider
                  value={rowRange}
                  onChange={(e, newValue) => setRowRange(newValue)}
                  valueLabelDisplay="auto"
                  min={rowRangeLimits.min}
                  max={rowRangeLimits.max}
                  step={10000}
                  marks={[
                    { value: rowRangeLimits.min, label: `${rowRangeLimits.min}` },
                    { value: rowRangeLimits.max, label: `${rowRangeLimits.max}` },
                  ]}
                />

                <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                  <TextField
                    label="Min Rows"
                    type="number"
                    value={rowRange[0]}
                    onChange={(e) => {
                      const newMin = parseInt(e.target.value, 10);
                      if (!isNaN(newMin)) {
                        // Ensure min doesn't exceed max
                        setRowRange(([_, max]) => [Math.min(newMin, max), max]);
                      }
                    }}
                    inputProps={{ min: rowRangeLimits.min, max: rowRangeLimits.max }}
                    fullWidth
                  />
                  <TextField
                    label="Max Rows"
                    type="number"
                    value={rowRange[1]}
                    onChange={(e) => {
                      const newMax = parseInt(e.target.value, 10);
                      if (!isNaN(newMax)) {
                        // Ensure max doesn't fall below min
                        setRowRange(([min, _]) => [min, Math.max(newMax, min)]);
                      }
                    }}
                    inputProps={{ min: rowRangeLimits.min, max: rowRangeLimits.max }}
                    fullWidth
                  />
                </Box>

                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selected Range: {rowRange[0].toLocaleString()} - {rowRange[1].toLocaleString()} rows
                </Typography>
              </Box> */}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setShowSupportDialog(false);
                }}
                color="primary"
              >
                Cancel
              </Button>
              {/* <Button
                onClick={() => {
                  handleContactSupport();
                  setShowSupportDialog(false);
                }}
                color="primary"
                variant="contained"
              >
                SEND REQUEST
              </Button>
              <Button
                onClick={() => setShowTable(true)}
                color="secondary"
                variant="outlined"
              >
                Show Requested Files
              </Button> */}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* KPI Calculation Modal */}
      <RTSKPICalculation
        kpiKey={activeKpiKey}
        kpiData={kpiData}
        onClose={() => setActiveKpiKey(null)}
      />
    </main>
  );
}

export default UKRecordKeepingRTS24OrdersAndExecutionsComponent;
